/**
 * launchWording.js - T28 twin of proxy-server/launch/wording.js (launch-redesign E2). The table is
 * deep-equal to the proxy's (launchWording.test.js); change docs/signoff-report-template.md first,
 * then both copies. Strings only; static hints name no app, vendor or request type; the no-rows
 * hint takes {scope, when} (E25). Never throws.
 */

export const WORDING = Object.freeze({
  statusLabels: Object.freeze({
    met: 'Met',
    not_met: 'Not met',
    not_covered: 'Not covered',
    needs_review: 'Needs review',
    not_run: 'Not run',
    manual: 'Manual review',
    waived: 'Waived - not verified',
  }),
  notMetDefinition: 'Not met: a violation was observed in the captured traffic.',
  notCoveredDefinition: 'Not covered: the capture holds no evidence either way. It is neither a pass nor a defect.',
  notCoveredRecapture: 'Not covered - needs re-capture',
  overallNotCovered: 'NOT READY. {x} of {N} criteria are not covered by this capture: {list}.',
  branchLine: '{label}: {status}',
  branchLineGraded: '{label}: {status} ({graded} graded, {unreadable} unreadable)',
  observedValue: 'Observed {param}: {value}',
  observedAbsent: 'Observed {param}: absent',
  observedUnreadable: 'Observed {param}: unreadable',
  interpretedAs: 'AC text: {text}; interpreted as field {field}.',
  noBranchApplies: 'Needs review: this request has {field} set to a value no branch covers.',
  roleRegistration: 'Graded on registration rows only; {n} session rows in this capture not graded.',
  roleSession: 'Graded on session rows only; {n} registration rows in this capture not graded.',
  shadowLabel: 'Met (unconfirmed: new status Not covered)',
  unconfirmedAnnotation: 'unconfirmed',
  waivedLabel: 'Waived - not verified',
  waivedHeader: '{n} branches not verified (waived)',
  waivedLine: 'Waived by {signer} on {time}: {reason}.',
  waivedVoidUnit: 'voided: unit changed',
  waivedVoidBuild: 'voided: build changed',
  hints: Object.freeze({
    no_rows_matched: 'Not covered: no request matching {scope} in this capture. Re-capture and run again.',
    no_rows_matched_when: 'Not covered: no request matching {scope} where {when} in this capture. Re-capture and run again.',
    no_rows_matched_bare: 'Not covered: no request matched in this capture. Re-capture and run again.',
    scope_unreadable: 'Not covered: the field that selects requests for this check could not be read on any request in this capture. Re-capture and run again.',
    unreadable_truncated_url: 'Not covered: the URL was cut off at about 4 KB, so this value cannot be read. Capture through the proxy where the app trusts it (debug build, or an emulator or device with the proxy certificate as a system CA).',
    unreadable_no_request_body: 'Not covered: USB capture records the request URL only, not the request body. Capture through the proxy where the app trusts it (debug build, or an emulator or device with the proxy certificate as a system CA). Android production builds ignore user certificates, so proxy capture of them will not decrypt.',
    unreadable_no_response: 'Not covered: USB capture has no response.',
    unreadable_body_stripped: 'Not covered: the response body was not kept for this request. Re-capture and run the check soon after.',
    observe_unread: 'Not covered: {item} was not readable on any request in this capture.',
  }),
});

/**
 * Dashboard-only strings (launch-redesign T34). NOT part of WORDING: the proxy has no twin for these (the
 * report never prints a button or a fallback notice), so launchWording.test.js's deep-equal of WORDING is
 * unaffected. Status labels, hints, the waived label and the shadow label all come from WORDING.
 */
export const UI_WORDING = Object.freeze({
  fallbackNotice: 'The proxy did not answer. This is the dashboard’s local fallback grade of the results held in this tab; it can differ from the proxy and is not the sign-off answer.',
  noAnswerNotice: 'The proxy did not answer and there are no local results to grade. Sign-off status is unavailable.',
  recapture: 'Re-capture and run again.',
  waiveAction: 'Waive',
  waiveTitle: 'Waive an uncovered branch',
  waiveExplain: 'This records that the branch was not verified. It does not mark it as passed, and it is void if the unit definition or the launch build changes.',
  waiveTarget: 'Branch: {criterion} / {unit}',
  waiveReasonLabel: 'Reason (typed, shown in the report)',
  waiveReasonHelp: 'At least {n} characters. {count} typed.',
  waiveSubmit: 'Record waiver',
  waiveSubmitting: 'Recording...',
  waiveCancel: 'Cancel',
  waiveErrors: Object.freeze({
    human_session_required: 'Waiving needs a signed-in person; an API token cannot waive.',
    launch_has_no_owner: 'This launch has no owner, so it cannot take a waiver.',
    unit_not_waivable: 'Only a Not covered branch can be waived.',
    redesign_off: 'Branch grading is off on this proxy, so there is nothing to waive.',
    reason_too_short: 'The reason is too short.',
    reason_too_long: 'The reason is too long.',
    not_found: 'The launch was not found.',
    unit_not_found: 'The branch no longer exists. Reload the launch.',
    criterion_not_found: 'The criterion no longer exists. Reload the launch.',
    fallback: 'The waiver was not recorded.',
  }),
  voidedBy: 'Previously waived by {signer}',
});

/** Replace `{name}` with slots[name]; an absent slot stays visible. Never throws. */
export function fill(template, slots) {
  if (typeof template !== 'string') return '';
  const s = slots && typeof slots === 'object' ? slots : {};
  return template.replace(/\{([A-Za-z]+)\}/g, (m, k) => (Object.prototype.hasOwnProperty.call(s, k) && s[k] != null ? String(s[k]) : m));
}

const nonBlank = (v) => (typeof v === 'string' && v.trim() ? v.trim() : '');

/** Capture-gap hint by `naReason`; null for a code that is not a coverage gap. */
export function captureGapHint(naReason, slots) {
  try {
    const h = WORDING.hints;
    const s = slots && typeof slots === 'object' ? slots : {};
    if (naReason === 'no_rows_matched') {
      const scope = nonBlank(s.scope);
      const when = nonBlank(s.when);
      if (!scope) return h.no_rows_matched_bare;
      return when ? fill(h.no_rows_matched_when, { scope, when }) : fill(h.no_rows_matched, { scope });
    }
    if (naReason === 'scope_unreadable' || naReason === 'unreadable_truncated_url' || naReason === 'unreadable_no_request_body'
      || naReason === 'unreadable_no_response' || naReason === 'unreadable_body_stripped') return h[naReason];
    return null;
  } catch {
    return null;
  }
}
